import React, { memo } from 'react';
import { EyeInvisibleOutlined, PushpinOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { paths } from '@app/constants';
import { sortByPropertyValue, transformSlashDate } from '@app/utils';
import { Namespace, usePermissions } from '@data/index';
import { QuestionType, QuestionView, VisibilityType } from '@data/sdk/match';
import { AntdTooltip, AntdTypography } from '@ui/components';
import { MoreTags, QuestionTag } from './components';

const { Text } = AntdTypography;
const TAGS_LIMIT = 5;

type QuestionProps = {
  question: QuestionView;
};

const typeSortOrder = {
  tag: 1,
  starter: 2,
  icdCode: 3,
};

const QuestionComponent = ({ question }: QuestionProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const location = useLocation();
  const { IS_REVIEWER } = usePermissions();

  // const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);

  // const openCreateCategory = () => {
  //   setIsCreateCategoryOpen(true);
  // };

  // const closeCreateCategory = () => {
  //   setIsCreateCategoryOpen(false);
  // };

  const sortedCategories = sortByPropertyValue(question.categories, 'type', typeSortOrder);

  return (
    <>
      <li className="relative">
        <Link
          state={{ from: location }}
          to={`${question.id}/${paths.questions_library.details}`}
          className="flex flex-col gap-2 p-4 rounded-default w-full border border-solid border-base-400 mb-2"
        >
          <Text className="text-base">
            {question.text}

            {question.visibilityType === VisibilityType.Hidden ||
            question.questionType === QuestionType.Distractor ? (
              <AntdTooltip
                title={
                  question.visibilityType === VisibilityType.Hidden
                    ? t('questions_library.question.hidden_question')
                    : t('questions_library.question.distractor_question')
                }
                placement="top"
              >
                <EyeInvisibleOutlined className="text-lg text-base-500 ml-2" />
              </AntdTooltip>
            ) : null}
            {question.visibilityType === VisibilityType.First ? (
              <AntdTooltip title={t('questions_library.question.first_question')}>
                <PushpinOutlined className="text-lg text-base-500 ml-2" />
              </AntdTooltip>
            ) : null}
          </Text>

          <div className="flex flex-wrap gap-1">
            {sortedCategories.slice(0, TAGS_LIMIT).map((category) => (
              <QuestionTag
                key={category.id}
                type={category.type}
                name={category.shortName || category.name}
              />
            ))}
            {sortedCategories.length > TAGS_LIMIT && !IS_REVIEWER && (
              <MoreTags id={question.id} count={question.categories.length - TAGS_LIMIT} />
            )}
            {/* {!IS_REVIEWER && <AddTagButton onClick={openCreateCategory} />} */}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Text className="text-base-700">
              {t('questions_library.question.answered')}: {question.answeredCount}
            </Text>
            <Text className="text-base-700">
              {t('questions_library.question.linked_trials')}: {question.trialCount}
            </Text>
            <Text className="text-base-700">
              {t('questions_library.question.skipped')}: {question.skippedCount}
            </Text>
            <Text className="text-base-700">
              {t('questions_library.question.icd_count')}: {question.icdCount}
            </Text>
            {typeof question.icdTrialCount === 'number' && (
              <Text className="text-base-700">
                {t('questions_library.question.icd_trial_count')}: {question.icdTrialCount}
              </Text>
            )}
            <Text className="text-base-700 self-end">
              {t('questions_library.question.updated')}: {transformSlashDate(question.updatedAt)}
            </Text>
          </div>
        </Link>
      </li>

      {/* TODO: maybe will add it back */}
      {/* <CreateCategory
        open={isCreateCategoryOpen}
        close={closeCreateCategory}
        questionId={question.id}
      /> */}
    </>
  );
};

export const Question = memo(QuestionComponent, (prevProps, nextProps) => {
  return (
    prevProps.question.id === nextProps.question.id &&
    prevProps.question.text === nextProps.question.text &&
    prevProps.question.categories.length === nextProps.question.categories.length &&
    prevProps.question.answeredCount === nextProps.question.answeredCount &&
    prevProps.question.trialCount === nextProps.question.trialCount &&
    prevProps.question.skippedCount === nextProps.question.skippedCount &&
    prevProps.question.icdCount === nextProps.question.icdCount &&
    prevProps.question.icdTrialCount === nextProps.question.icdTrialCount &&
    prevProps.question.updatedAt === nextProps.question.updatedAt &&
    prevProps.question.visibilityType === nextProps.question.visibilityType &&
    prevProps.question.questionType === nextProps.question.questionType
  );
});
